/**
 * O prazo da lei, quando a intimação não diz o prazo (docs/PLANO_PRAZO_SEM_TEXTO.md).
 *
 * O backend lê o ato (sentença, acórdão, decisão…) e o rito, e grava os prazos da lei
 * em `prazo_presumido`. O principal (o recurso; os embargos de declaração só quando são
 * o único) vira o vencimento da intimação: `prazo_calculo` com `origem: 'presumido'`,
 * que a régua e a agenda já leem. Aqui, só como DIZER isso na tela.
 */
import type { ComunicacaoProcessualItem, PrazoCalculo, PrazoLegalPresumido } from '@/api/client';

/** O vencimento veio da lei, não do texto. */
export function ehPresumido(calculo: PrazoCalculo | null | undefined): boolean {
  return calculo?.origem === 'presumido';
}

/** "15 dias úteis" / "5 dias corridos". */
export function diasDoPrazo(dias: number | null, regime: 'uteis' | 'corridos' | null): string {
  // Sem dias: a data fixada pelo juiz — não há contagem a dizer.
  if (dias == null) return 'data fixada na intimação';
  return `${dias} ${dias === 1 ? 'dia' : 'dias'} ${regime === 'corridos' ? (dias === 1 ? 'corrido' : 'corridos') : dias === 1 ? 'útil' : 'úteis'}`;
}

/** A providência como a lei a dá ("Apelação"). Sem "se cabível": o cabimento é juízo
 *  do advogado, e a ressalva específica (o rol do 1.015…) vai à parte. */
export function providenciaDoPrazo(p: { providencia?: string }): string {
  return p.providencia ?? 'Prazo';
}

const LIMITE_DO_TRECHO = 180;

// O fim da frase: ponto ou ponto e vírgula seguido de espaço (ou o fim), fora das
// abreviações de tratamento e de citação ("Sr. Ministro", "art. 1.009"), ou a quebra de linha.
const FIM_DA_FRASE = /(?<!\b(?:Sr|Sra|Srs|Dr|Dra|Exmo|Exma|Min|art|arts|fls|n|inc|al|p))[.;](?=\s|$)|\n/i;
// Quanto voltar, no máximo, para pegar o começo da frase ("Diante do exposto, JULGO…").
const VOLTA_ATE_A_FRASE = 40;

/**
 * A citação do ato ("Diante do exposto, JULGO IMPROCEDENTE o pedido."): do começo da frase
 * — se está logo antes — ou do próprio trecho que o decidiu, até o fim da frase, sem passar
 * do limite. Curta de propósito: o texto inteiro está logo abaixo, com o trecho destacado.
 * Em palavra inteira e com reticências onde o texto continua — é citação de ato judicial.
 */
export function trechoDoAto(
  texto: string | null | undefined,
  trecho: { posicao: number; tamanho: number } | null | undefined,
): string | null {
  if (!texto || !trecho) return null;
  const fimDoAto = trecho.posicao + trecho.tamanho;
  const antes = texto.slice(Math.max(0, trecho.posicao - VOLTA_ATE_A_FRASE), trecho.posicao);
  const fimDaAnterior = Math.max(antes.lastIndexOf('. '), antes.lastIndexOf('; '), antes.lastIndexOf('\n'));
  const comecoDoTexto = trecho.posicao <= VOLTA_ATE_A_FRASE && fimDaAnterior < 0;
  const inicio = comecoDoTexto
    ? 0
    : fimDaAnterior >= 0
      ? trecho.posicao - antes.length + fimDaAnterior + 1
      : trecho.posicao;
  const depois = texto.slice(fimDoAto, Math.max(fimDoAto, trecho.posicao + LIMITE_DO_TRECHO));
  const ponto = depois.search(FIM_DA_FRASE);
  const fim = ponto >= 0 ? fimDoAto + ponto + 1 : fimDoAto + depois.length;
  const bruto = texto.slice(inicio, fim).replace(/\s+/g, ' ').trim();
  const cortadoNoInicio = inicio > 0 && fimDaAnterior < 0;
  const cortadoNoFim = ponto < 0 && fim < texto.trimEnd().length;
  const semFimPartido = cortadoNoFim ? bruto.replace(/\s+\S*$/, '') : bruto;
  return `${cortadoNoInicio ? '…' : ''}${semFimPartido}${cortadoNoFim ? '…' : ''}`;
}

/** Os prazos da lei além do principal (os embargos de declaração, os condicionais a mais). */
export function prazosSecundarios(com: Pick<ComunicacaoProcessualItem, 'prazo_presumido'>): PrazoLegalPresumido[] {
  return (com.prazo_presumido?.prazos ?? []).filter((p) => !p.principal);
}

/** O que o ato decide sobre o prazo quando não há prazo presumido — ou `null`. */
export function semPrazoOuIndefinido(
  com: Pick<ComunicacaoProcessualItem, 'prazo_estado' | 'prazo_presumido'>,
): { titulo: string; detalhe: string | null } | null {
  const dados = com.prazo_presumido;
  if (com.prazo_estado === 'sem_prazo' && dados?.ia) {
    // A leitura da IA concluiu que não há prazo para a parte (ex.: o do texto é do perito).
    return {
      titulo: 'Pela leitura da IA, este ato não abre prazo para a parte — confira.',
      detalhe: dados.confira ? (dados.motivos_ia ?? []).join(' ') || null : null,
    };
  }
  if (com.prazo_estado === 'sem_prazo') {
    // O despacho sem ordem à parte pode ter ordem que a leitura não pegou: "confira".
    if (dados?.ato === 'despacho') {
      return { titulo: 'Sem ordem à parte no texto — confira.', detalhe: null };
    }
    // A leitura do ato é do texto: diz de onde vem a conclusão, e pede a conferência.
    return {
      titulo: 'Pela leitura do texto, este ato não abre prazo para a parte — confira.',
      detalhe: dados?.ressalvas?.[0] ?? null,
    };
  }
  if (com.prazo_estado === 'indefinido') {
    return {
      titulo: 'Não identificamos o prazo — confira no tribunal.',
      detalhe: dados?.motivo ?? null,
    };
  }
  return null;
}

/** A ressalva de quem decidiu o prazo: a leitura da IA (com os motivos do "confira")
 *  ou a conta do sistema pelo texto. */
export function ressalvaDaLeitura(calculo: {
  origem?: string;
  ia?: boolean;
  confira?: boolean;
  motivos_ia?: string[];
  data_fixada?: boolean;
}): string | null {
  if (!calculo.ia) return null;
  const quem =
    calculo.origem === 'presumido'
      ? 'A intimação não diz o prazo: a IA indicou o da lei para este ato — confira no tribunal.'
      : calculo.data_fixada
        ? 'Data fixada na própria intimação, lida pela IA — confira no tribunal.'
        : 'Prazo lido pela IA no texto da intimação — confira no tribunal.';
  const motivos = calculo.confira ? (calculo.motivos_ia ?? []).join(' ') : '';
  return [quem, motivos].filter(Boolean).join(' ');
}
